'use client';

import { useEffect, useRef, useState } from 'react';

import { Trash } from '@/icons/Trash';
import { getBasket, getCheckoutState, setBasket, setLastPaymentType } from '@/lib/BasketStorage';
import { APPOINTMENT_PRIMARY_CTA } from '@/lib/basketUtils';
import { matchesDiscountBin, normalizeCardBin } from '@/lib/CardBinDiscountUtils';
import { CheckoutFormField } from '@/lib/CheckoutFormField';
import { formatCreditCardNumber } from '@/lib/CreditCardUtils';
import { Endpoints } from '@/lib/Endpoints';
import { ErrorHandler } from '@/lib/ErrorHandler';
import { StatusCode } from '@/lib/StatusCode';
import { sendGTMEvent } from '@/lib/gtm-wrapper';
import { getBasketEcommerceBody, GTMEventType } from '@/lib/GTMEventType';
import { getIsAnonymous } from '@/lib/LocalStorage';
import { humanizeMasterpassError } from '@/lib/masterpassError';
import { reportCheckoutBError } from '@/lib/reportCheckoutBError';
import { Requester } from '@/lib/Requester';
import { safeMultiply } from '@/lib/utils';
import { WebLink } from '@/lib/WebLinks';
import { Basket, BasketResponseModel } from '@/models/Basket';
import { GenericResponse } from '@/models/GenericResponse';
import {
  MasterpassCard,
  MasterpassTokenResponse,
  OrderPaymentInformation,
  OrderResponse,
} from '@/models/Order';
import { PaymentType } from '@/models/PaymentType';
import { CreditCard, PlusIcon } from 'lucide-react';
import { useRouter } from 'next/navigation';
import Script from 'next/script';
import CheckoutBPaymentErrorDialog from './checkout-b/CheckoutBPaymentErrorDialog';
import { CheckoutPaymentProblem } from './CheckoutPaymentProblem';
import MasterpassAddCardDialog from './modals/MasterpassAddCardDialog';
import MasterpassCardForm from './modals/MasterpassCardForm';
import MasterpassConnectDialog from './modals/MasterpassConnectDialog';
import MasterpassCVVDialog from './modals/MasterpassCVVDialog';
import MasterpassLoadingDialog from './modals/MasterpassLoadingDialog';
import MasterpassOTPDialog from './modals/MasterpassOTPDialog';
import MasterpassRemoveCardDialog from './modals/MasterpassRemoveCardDialog';
import SmartImage from './SmartImage';
import { Button } from './ui/button';
import { Spinner } from './ui/spinner';
import { useToast } from './ui/use-toast';
import {
  getPreferManualCard,
  MP_PREFER_MANUAL_CARD_KEY,
  SavedCardsLoadFailedDialog,
  setPreferManualCard,
} from './SavedCardsLoadFailedDialog';

function readCheckoutBSenderPhone() {
  try {
    const raw = sessionStorage.getItem('hzc_checkout_b_sender');
    if (!raw) return '';
    const parsed = JSON.parse(raw) as { phone?: string };
    return parsed?.phone ? String(parsed.phone) : '';
  } catch {
    return '';
  }
}

/** Telefon gönderen alanından gelir: A checkout state, sepet, B adım kaydı. */
function resolveMasterpassPhone() {
  const fromState = getCheckoutState()?.formFields?.[CheckoutFormField.SENDER_PHONE];
  if (fromState) return fromState;
  const fromBasket = getBasket()?.senderInformation?.phoneNumber;
  if (fromBasket) return fromBasket;
  return readCheckoutBSenderPhone();
}

function isSenderMissing(error: unknown) {
  const code = (error as { response?: { data?: { meta?: { statusCode?: number } } } })?.response
    ?.data?.meta?.statusCode;
  return Number(code) === StatusCode.SENDER_INFORMATION_MISSING;
}

function resolveSenderFullName() {
  return (
    getCheckoutState()?.formFields?.[CheckoutFormField.SENDER_FULL_NAME] ||
    getBasket()?.senderInformation?.title ||
    ''
  );
}

function masterpassChargeTl(order: {
  total?: number;
  paymentInformation?: OrderPaymentInformation;
}) {
  const frozen = Number(order.paymentInformation?.card?.chargeAmount);
  if (Number.isFinite(frozen) && frozen > 0) return frozen;
  const miles = order.paymentInformation?.miles;
  const open = Boolean(miles?.issuedAt && !miles.reversedAt && Number(miles.quantity) > 0);
  const stored = Number(order.paymentInformation?.card?.amount);
  if (open && Number.isFinite(stored) && stored > 0) return stored;
  if (open) {
    const milesTl = Number(miles?.milesTl ?? miles?.quantity ?? 0);
    return Math.max(0, Number(order.total || 0) - milesTl);
  }
  return Number(order.total) || 0;
}

export function CheckoutPaymentTypeMasterpass({
  paymentType,
  paymentInformation,
  setPaymentInformation,
  paymentTypes,
  paymentSubmitLabel,
  checkoutB,
  milesHoldClosed,
}: {
  paymentType: number;
  paymentInformation?: OrderPaymentInformation;
  setPaymentInformation: (data: OrderPaymentInformation) => void;
  total?: number;
  paymentTypes: PaymentType[];
  /** Araç süsleme randevu ödemesi: varsayılan "Ödeme Yap" yerine */
  paymentSubmitLabel?: string;
  checkoutB?: boolean;
  milesHoldClosed?: boolean;
}) {
  const router = useRouter();

  enum addCardDialogSteps {
    noForm,
    cardForm,
    otpForm,
    cvvForm,
    removeCardForm,
    connectAccount,
  }
  const [dialogStep, setDialogStep] = useState<addCardDialogSteps>(addCardDialogSteps.noForm);

  enum flowTypes {
    none,
    addCard,
    removeCard,
    payment,
    linkToMerchant,
    payAndLink,
  }
  const [flowType, setFlowType] = useState<flowTypes>(flowTypes.none);

  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isSdkReady, setIsSdkReady] = useState(false);
  const [serviceData, setServiceData] = useState<MasterpassTokenResponse | null>(null);
  const [tokenData, setTokenData] = useState<any | null>(null);
  const [cardsGotten, setCardsGotten] = useState(false);
  const [cards, setCards] = useState<MasterpassCard[]>([]);
  const [selectedCard, setSelectedCard] = useState<MasterpassCard | null>(null);
  const [addingCard, setAddingCard] = useState(false);
  const [verifyingOTP, setVerifyingOTP] = useState(false);
  const [verifyingCVV, setVerifyingCVV] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [removingCardAlias, setRemovingCardAlias] = useState<string | null>(null);
  const [isRemovingCardAlias, setIsRemovingCardAlias] = useState(false);
  const [orderId, setOrderId] = useState<string | null | undefined>(null);
  const [userIsAnonymous, setUserIsAnonymous] = useState<boolean | undefined>(undefined);
  const [isUserConnected, setIsUserConnected] = useState(false);
  const [otpVCount, setOtpVCount] = useState(0);
  const [cvvVCount, setCvvVCount] = useState(0);
  const [removeVCount, setRemoveVCount] = useState(0);
  const [cardCandidate, setCardCandidate] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isItLoading, setIsItLoading] = useState(true);
  const [serviceReturnUrl, setServiceReturnUrl] = useState<string | null>(null);
  const [localBasket, setLocalBasket] = useState<Basket | undefined>(undefined);
  const [discountBins, setDiscountBins] = useState<string[]>([]);
  const [activeCardNumber, setActiveCardNumber] = useState('');
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const paymentErrorTimer = useRef<number | null>(null);
  const payAttemptRef = useRef(false);

  const discountBinsRef = useRef<string[]>([]);
  const appliedCardBinRef = useRef<string | null>(null);
  const cardNumberDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Kart listesi kurulumu: 10sn timeout → 1 otomatik retry → manuel kart formu */
  const SETUP_TIMEOUT_MS = 10_000;
  const MAX_SETUP_ATTEMPTS = 2;
  const [cardsListFailed, setCardsListFailed] = useState(false);
  /** fail sonrası: dialog göster (choose) mi, yoksa misafir gibi kart formu (manual) mu */
  const [cardsFailMode, setCardsFailMode] = useState<'choose' | 'manual' | null>(null);
  const [cardsRetrying, setCardsRetrying] = useState(false);
  const setupTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const setupAttemptRef = useRef(0);
  const setupGenerationRef = useRef(0);
  const cardsRequestIdRef = useRef(0);
  const cardListRetryRef = useRef(0);
  const getCardsRef = useRef<() => void>(() => {});
  const cardsGottenRef = useRef(false);
  const setupSettledRef = useRef(false);
  const cardsListFailedRef = useRef(false);

  const flowTypeRef = useRef(flowType);
  useEffect(() => {
    flowTypeRef.current = flowType;
  }, [flowType]);

  const orderIdRef = useRef(orderId);
  useEffect(() => {
    orderIdRef.current = orderId;
  }, [orderId]);

  const cardCandidateRef = useRef(cardCandidate);
  useEffect(() => {
    cardCandidateRef.current = cardCandidate;
  }, [cardCandidate]);

  useEffect(() => {
    cardsGottenRef.current = cardsGotten;
  }, [cardsGotten]);

  useEffect(() => {
    return () => {
      if (setupTimeoutRef.current) {
        clearTimeout(setupTimeoutRef.current);
        setupTimeoutRef.current = null;
      }
      if (paymentErrorTimer.current) {
        window.clearTimeout(paymentErrorTimer.current);
        paymentErrorTimer.current = null;
      }
    };
  }, []);

  const clearStates = () => {
    setDialogStep(addCardDialogSteps.noForm);
    // setIsPaying(false);
    setAddingCard(false);
    setVerifyingOTP(false);
    setVerifyingCVV(false);
    setRemovingCardAlias(null);
    setIsRemovingCardAlias(false);
    setIsItLoading(false);
  };

  const clearSetupTimeout = () => {
    if (setupTimeoutRef.current) {
      clearTimeout(setupTimeoutRef.current);
      setupTimeoutRef.current = null;
    }
  };

  const settleSetupSuccess = () => {
    setupSettledRef.current = true;
    cardsListFailedRef.current = false;
    // In-flight retry / token isteklerini iptal say
    setupGenerationRef.current += 1;
    clearSetupTimeout();
    setCardsListFailed(false);
    setCardsFailMode(null);
    setCardsRetrying(false);
    setPreferManualCard(MP_PREFER_MANUAL_CARD_KEY, false);
  };

  const applyManualCardFallback = (showChooser: boolean) => {
    setupSettledRef.current = true;
    cardsListFailedRef.current = true;
    clearSetupTimeout();
    // In-flight accountAccess callback'lerini geçersiz kıl
    cardsRequestIdRef.current += 1;
    setupGenerationRef.current += 1;
    setCardsListFailed(true);
    setCardsGotten(true);
    setCards([]);
    setSelectedCard(null);
    setIsUserConnected(false);
    setIsLoading(false);
    setIsItLoading(false);
    setIsConnecting(false);
    setDialogStep(addCardDialogSteps.noForm);
    setCardsRetrying(false);
    if (showChooser) {
      setCardsFailMode('choose');
    } else {
      setPreferManualCard(MP_PREFER_MANUAL_CARD_KEY, true);
      setCardsFailMode('manual');
    }
  };

  const fallbackToManualCardForm = () => {
    applyManualCardFallback(false);
  };

  const retryListSavedCards = () => {
    setPreferManualCard(MP_PREFER_MANUAL_CARD_KEY, false);
    setCardsFailMode(null);
    setCardsListFailed(false);
    cardsListFailedRef.current = false;
    setCardsRetrying(true);
    void setMasterpassToken();
  };

  const choosePayWithCardDetails = () => {
    setPreferManualCard(MP_PREFER_MANUAL_CARD_KEY, true);
    setCardsFailMode('manual');
  };

  const setTypeForGTM = () => {
    const basket = localBasket || getBasket();
    setLastPaymentType('Kredi Kartı - Masterpass');
    if (basket) {
      sendGTMEvent({
        event: GTMEventType.ADD_PAYMENT_INFO,
        ecommerce: {
          ...getBasketEcommerceBody(basket),
          payment_type: 'Kredi Kartı - Masterpass',
        },
      });
    }
  };

  const logB = (
    action: string,
    message: string,
    kind: 'api' | 'validation' | 'unexpected' = 'api',
    error?: unknown,
  ) => {
    reportCheckoutBError({
      flow: checkoutB ? 'checkout-b' : 'checkout-a',
      step: 'payment',
      action,
      kind,
      message,
      error,
    });
  };

  const aError = (description?: string, error?: unknown) => {
    const raw = String(description || '')
      .replace(/\s+/g, ' ')
      .trim();
    const msg = humanizeMasterpassError({ messages: [raw] });
    logB('masterpass', raw && raw !== msg ? `${msg} — ${raw}` : msg, 'api', error);
    toast({
      variant: 'destructive',
      description: msg,
    });
    setIsPaying(false);
    setIsLoading(false);
    setIsItLoading(false);
  };

  const showSenderMissing = (error: unknown) => {
    const { message } = ErrorHandler.getMessage(error);
    toast({
      variant: 'destructive',
      description: message,
    });
    setIsPaying(false);
    setIsLoading(false);
    setIsItLoading(false);
  };

  const failPayment = (description?: string, codes?: Array<string | number | null | undefined>) => {
    const msg = humanizeMasterpassError({ codes, messages: [description] });
    logB('masterpass-pay', msg);
    const isPay =
      flowTypeRef?.current === flowTypes.payment || flowTypeRef?.current === flowTypes.payAndLink;
    if (!isPay || verifyingOTP || verifyingCVV) {
      toast({ variant: 'destructive', description: msg });
      setVerifyingOTP(false);
      setVerifyingCVV(false);
      setIsPaying(false);
      return;
    }
    if (checkoutB) {
      setIsPaying(false);
      if (paymentErrorTimer.current) window.clearTimeout(paymentErrorTimer.current);
      paymentErrorTimer.current = window.setTimeout(() => {
        paymentErrorTimer.current = null;
        setPaymentError(msg);
      }, 40);
      return;
    }
    router.replace(`${WebLink.ORDER_PAYMENT_FAILED}?reason=${encodeURIComponent(msg)}`);
  };

  const masterPassSDKLoaded = (status: boolean) => {
    if (status) {
      setIsSdkReady(true);
      if (serviceData) {
        window.Masterpass.setEndpoint(serviceData?.serviceUrl);
        window.Masterpass.setToken(serviceData?.accessToken);
        window.Masterpass.setMerchantId(tokenData?.MerchantId);
        setServiceReturnUrl(serviceData?.returnUrl);
      }
      return;
    }
  };

  const cleanPhoneNumber = (phone: string | undefined) => {
    if (!phone) {
      return '';
    }
    return phone.replace(/[^0-9]/g, '');
  };

  const syncCardDiscountByBin = async (rawBin?: string | null) => {
    const normalized = normalizeCardBin(rawBin);
    const shouldApply = Boolean(
      normalized && matchesDiscountBin(normalized, discountBinsRef.current),
    );

    if (shouldApply && appliedCardBinRef.current === normalized) {
      return;
    }
    if (!shouldApply && !appliedCardBinRef.current) {
      return;
    }

    try {
      if (shouldApply && normalized) {
        const { data: result } = await Requester.post<GenericResponse<BasketResponseModel>>(
          Endpoints.BASKET_APPLY_CARD_DISCOUNT,
          { bin: normalized },
        );
        if (result.data.basket) {
          setBasket(result.data.basket, 'Masterpass - applyCardDiscount');
          setLocalBasket(result.data.basket);
        }
        appliedCardBinRef.current = normalized;
        if (result.data.cardDiscount?.applied && result.data.cardDiscount.title) {
          toast({
            description: `${result.data.cardDiscount.title} indirimi uygulandı`,
          });
        }
        return;
      }

      const { data: result } = await Requester.post<GenericResponse<BasketResponseModel>>(
        Endpoints.BASKET_REMOVE_CARD_DISCOUNT,
      );
      if (result.data.basket) {
        setBasket(result.data.basket, 'Masterpass - removeCardDiscount');
        setLocalBasket(result.data.basket);
      }
      appliedCardBinRef.current = null;
    } catch {
      // Sepet indirimi opsiyonel; ödeme akışını bloklamayalım.
    }
  };

  const handleCardNumberChange = (cardNumber: string) => {
    setActiveCardNumber(cardNumber);
    if (cardNumberDebounceRef.current) {
      clearTimeout(cardNumberDebounceRef.current);
    }
    cardNumberDebounceRef.current = setTimeout(() => {
      syncCardDiscountByBin(cardNumber);
    }, 400);
  };

  const getPaymentCardBin = (values?: { cardNumber?: string }) => {
    if (selectedCard?.cardBin) {
      return normalizeCardBin(selectedCard.cardBin);
    }
    if (values?.cardNumber) {
      return normalizeCardBin(values.cardNumber);
    }
    if (activeCardNumber) {
      return normalizeCardBin(activeCardNumber);
    }
    return appliedCardBinRef.current;
  };

  const getMasterpassToken = async ({
    withthreed,
    keepError,
  }: {
    withthreed?: boolean;
    keepError?: boolean;
  }) => {
    try {
      // Üye: hesap telefonu. Misafir: checkout state, sepet göndericisi veya B adımının kaydı.
      const phoneForToken = resolveMasterpassPhone();
      const { data: result } = await Requester.get<GenericResponse<MasterpassTokenResponse>>(
        `${Endpoints.MASTERPASS_GET_TOKEN}?phoneNumber=${cleanPhoneNumber(phoneForToken)}`,
      );
      if (result?.data?.accessToken && result?.data?.serviceUrl && window.Utils?.decodeJwt) {
        const bins = result.data.discountBins || [];
        discountBinsRef.current = bins;
        setDiscountBins(bins);
        setServiceData(result?.data);
        const decoded = window.Utils.decodeJwt(result?.data?.accessToken);
        setTokenData(decoded);
        if (window.Masterpass) {
          window.Masterpass.setEndpoint(result?.data?.serviceUrl);
          window.Masterpass.setToken(result?.data?.accessToken);
          window.Masterpass.setMerchantId(decoded.MerchantId);
          setServiceReturnUrl(result?.data?.returnUrl);
        }
        return { token: decoded, service: result.data };
      }
      return null;
    } catch (error) {
      if (isSenderMissing(error) || keepError) {
        throw error;
      }
      return null;
    }
  };

  const takePaySession = async () => {
    try {
      return await getMasterpassToken({ withthreed: true, keepError: true });
    } catch (error) {
      if (isSenderMissing(error)) throw error;
      const failed = new Error('payment-session');
      (failed as Error & { cause?: unknown }).cause = error;
      throw failed;
    }
  };

  const failPaySession = (error: unknown) => {
    const sessionMiss = error instanceof Error && error.message === 'payment-session';
    if (sessionMiss) {
      aError(
        'Ödeme oturumu alınamadı. Lütfen tekrar deneyin.',
        (error as { cause?: unknown }).cause,
      );
      return;
    }
    aError(ErrorHandler.getMessage(error).message, error);
  };

  const scheduleSetupTimeout = () => {
    clearSetupTimeout();
    if (setupSettledRef.current) {
      return;
    }
    setupTimeoutRef.current = setTimeout(() => {
      void failSetupAttempt();
    }, SETUP_TIMEOUT_MS);
  };

  const runSetupAttempt = async () => {
    if (setupSettledRef.current) {
      return;
    }
    const generation = ++setupGenerationRef.current;
    setIsLoading(true);
    setCardsGotten(false);
    cardsGottenRef.current = false;
    scheduleSetupTimeout();

    try {
      const getTokenStatus = await getMasterpassToken({ withthreed: false });
      if (generation !== setupGenerationRef.current || setupSettledRef.current) {
        return;
      }
      if (!getTokenStatus) {
        await failSetupAttempt();
        return;
      }
      if (window.Masterpass) {
        setIsSdkReady(true);
        setIsLoading(false);
      }
      // SDK henüz yoksa isLoading true kalır; timeout / SDK onLoad sonrası getCards tamamlar
    } catch (error) {
      if (generation !== setupGenerationRef.current || setupSettledRef.current) {
        return;
      }
      if (isSenderMissing(error)) {
        showSenderMissing(error);
        return;
      }
      await failSetupAttempt();
    }
  };

  const failSetupAttempt = async () => {
    if (setupSettledRef.current || cardsGottenRef.current) {
      return;
    }
    clearSetupTimeout();
    // Eski token / accountAccess cevaplarını yok say
    setupGenerationRef.current += 1;
    cardsRequestIdRef.current += 1;
    setupAttemptRef.current += 1;

    if (setupAttemptRef.current < MAX_SETUP_ATTEMPTS) {
      await runSetupAttempt();
      return;
    }
    fallbackToManualCardForm();
  };

  const setMasterpassToken = async () => {
    setupAttemptRef.current = 0;
    setupSettledRef.current = false;
    cardsListFailedRef.current = false;
    setCardsListFailed(false);
    await runSetupAttempt();
  };

  useEffect(() => {
    setUserIsAnonymous(getIsAnonymous());
    setLocalBasket(getBasket());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userIsAnonymous === undefined) {
      return;
    }
    if (userIsAnonymous) {
      setIsLoading(false);
      setIsItLoading(false);
      // Misafir: kart listesi yok; token ödeme anında alınır
      void getMasterpassToken({ withthreed: false }).catch((error) => {
        if (isSenderMissing(error)) {
          showSenderMissing(error);
          return;
        }
        logB('masterpass-token', 'Masterpass oturumu alınamadı.', 'unexpected', error);
      });
      return;
    }
    // Önceki oturumda "kart ile öde" seçildiyse listelemeyi atla → misafir formu
    if (getPreferManualCard(MP_PREFER_MANUAL_CARD_KEY)) {
      applyManualCardFallback(false);
      void getMasterpassToken({ withthreed: false }).catch((error) => {
        if (isSenderMissing(error)) {
          showSenderMissing(error);
          return;
        }
        logB('masterpass-token', 'Masterpass oturumu alınamadı.', 'unexpected', error);
      });
      return;
    }
    void setMasterpassToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIsAnonymous]);

  const listCardsResponseHandler = (
    statusCode: any,
    response: {
      statusCode: number;
      description: any;
      correlationId: any;
      responseCode: string;
      code: string;
      result: { cards: any[] };
      exception?: any;
    },
  ) => {
    settleSetupSuccess();
    setCardsGotten(true);
    setIsConnecting(false);

    const accountCode = response?.responseCode || response?.code || response?.exception?.code;
    const accountMessage = String(response?.exception?.message || '');
    const masterpassUserMissing =
      accountCode === 'ACCOUNT_NOT_FOUND' ||
      accountCode === 'USER_NOT_FOUND' ||
      (/kullan/i.test(accountMessage) && /bulunamad/i.test(accountMessage));
    if (masterpassUserMissing) {
      setCards([]);
      return;
    }

    if (
      response?.statusCode !== 200 &&
      response?.exception?.message &&
      response?.exception?.code !== 'ACCOUNT_NOT_LINKED_TO_MERCHANT'
    ) {
      logB('list-cards', String(response.exception.message));
      toast({
        variant: 'destructive',
        description: response?.exception?.message,
      });
      return;
    }

    if (
      response?.responseCode === 'ACCOUNT_NOT_LINKED_TO_MERCHANT' ||
      response?.code === 'ACCOUNT_NOT_LINKED_TO_MERCHANT' ||
      response?.exception?.code === 'ACCOUNT_NOT_LINKED_TO_MERCHANT'
    ) {
      setDialogStep(addCardDialogSteps.connectAccount);
      return;
    }

    clearStates();
    // setIsPaying(false);

    if (response.statusCode != 200) {
      return;
    }

    setIsUserConnected(true);

    if (response?.result?.cards) {
      const list = response.result.cards;
      const lookingForNewCard = Boolean(cardCandidateRef.current);
      const matchedCard = list.find((card) => card.uniqueCardNumber === cardCandidateRef?.current);
      // Masterpass yeni kartı listeye hemen yazmayabiliyor; kısa aralıkla tekrar dene.
      if (lookingForNewCard && !matchedCard && cardListRetryRef.current < 3) {
        cardListRetryRef.current += 1;
        getCardsRef.current();
        return;
      }
      cardListRetryRef.current = 0;
      cardCandidateRef.current = null;
      setCardCandidate(null);
      setCards(list);
      setSelectedCard(matchedCard ?? (lookingForNewCard ? list[list.length - 1] : undefined));
      return;
    }

    setCards([]);
  };

  const onPopupClose = () => {
    setDialogStep(addCardDialogSteps.noForm);
    setIsItLoading(false);
  };

  const getCards = () => {
    setTimeout(() => {
      getCardsInit();
    }, 500);
  };
  getCardsRef.current = getCards;

  const getCardsInit = () => {
    if (userIsAnonymous) {
      return;
    }
    // Timeout sonrası manuel forma düştüysek tekrar kart listesi çekme
    if (cardsListFailedRef.current) {
      return;
    }
    clearStates();
    // setIsPaying(false);

    if (serviceData && isSdkReady) {
      setIsLoading(false);
      if (window.Masterpass) {
        const requestId = ++cardsRequestIdRef.current;
        const paylod = {
          accountKey: tokenData.AccountKey,
          accountKeyType: 'Msisdn',
          userId: tokenData.UserId,
          terminalGroupId: tokenData.terminalGroupId,
        };
        window.Masterpass.accountService.accountAccess(paylod, (statusCode, response) => {
          if (cardsListFailedRef.current) {
            return;
          }
          // Kurulum bittikten sonra kart ekleme/silme listesini yeniler.
          // setupSettled yalnızca daha eski, artık geçersiz istekleri yutar.
          if (setupSettledRef.current && requestId !== cardsRequestIdRef.current) {
            return;
          }
          // Retry sırasında gelen eski cevap: kullanılabilirse kabul et (kartlar gelmiş olabilir)
          if (requestId !== cardsRequestIdRef.current) {
            const code = response?.responseCode || response?.code || response?.exception?.code;
            const usable =
              response?.statusCode === 200 ||
              code === 'ACCOUNT_NOT_FOUND' ||
              code === 'USER_NOT_FOUND' ||
              code === 'ACCOUNT_NOT_LINKED_TO_MERCHANT';
            if (!usable) {
              return;
            }
          }
          listCardsResponseHandler(statusCode, response);
        });
      }
    }
  };

  const showOTPForm = () => {
    setOtpVCount((prev) => prev + 1);
    setVerifyingOTP(false);
    setIsPaying(false);
    setDialogStep(addCardDialogSteps.otpForm);
  };

  const linkToMerchantHandler = (status: any, response: any) => {
    if (response?.statusCode !== 200 && response?.exception?.message) {
      logB('link-account', String(response.exception.message));
      toast({
        variant: 'destructive',
        description: response?.exception?.message,
      });
      return;
    }

    switch (response.responseCode || response.result.responseCode) {
      case '0000':
        setIsItLoading(true);
        setIsUserConnected(true);
        setDialogStep(addCardDialogSteps.noForm);
        getCards();
        break;
      case '5001':
        showOTPForm();
        break;
      case '5008':
        showOTPForm();
        break;
      default:
        aError(
          response?.result?.responseDescription ||
            response?.result?.description ||
            response?.exception?.message ||
            response?.description ||
            'Hesap bağlanamadı. Lütfen tekrar deneyin.',
        );
    }
  };

  const linkToMerchant = () => {
    if (userIsAnonymous) {
      return;
    }
    setIsConnecting(true);
    setFlowType(flowTypes.linkToMerchant);
    clearStates();
    if (serviceData && isSdkReady) {
      setIsLoading(false);
      setIsItLoading(true);
      if (window.Masterpass) {
        const paylod = {
          accountKey: tokenData.AccountKey,
          accountKeyType: 'Msisdn',
          userId: tokenData.UserId,
          terminalGroupId: tokenData.terminalGroupId,
        };
        window.Masterpass.accountService.linkToMerchant(paylod, linkToMerchantHandler);
      }
    }
  };

  const startRemoval = (alias: string) => {
    setRemovingCardAlias(alias);
    setDialogStep(addCardDialogSteps.removeCardForm);
  };

  const removeCard = async (alias: string) => {
    clearStates();
    setIsRemovingCardAlias(true);
    setFlowType(flowTypes.removeCard);
    try {
      await getMasterpassToken({ withthreed: false });
    } catch (error) {
      if (isSenderMissing(error)) {
        showSenderMissing(error);
      }
      setIsRemovingCardAlias(false);
      return;
    }
    if (!serviceData || !isSdkReady || !window?.Masterpass) {
      setIsRemovingCardAlias(false);
      return;
    }
    setRemovingCardAlias(alias);
    const paylod = {
      cardAlias: alias,
      accountKey: tokenData.AccountKey,
      userId: tokenData.UserId,
    };
    window.Masterpass.accountService.removeCard(paylod, mfsRemoveResponseHandler);
  };

  const rejectConnection = () => {
    clearStates();
    setDialogStep(addCardDialogSteps.noForm);
  };

  const payWithCard = async () => {
    if (milesHoldClosed || !selectedCard || payAttemptRef.current) {
      return;
    }
    payAttemptRef.current = true;
    clearStates();
    setTypeForGTM();
    setIsPaying(true);
    setFlowType(flowTypes.payment);

    try {
      const session = await takePaySession();
      if (!window?.Masterpass) {
        aError('Masterpass hazır değil. Lütfen tekrar deneyin.');
        return;
      }
      if (!session?.token?.AccountKey || !session.service) {
        aError('Ödeme oturumu alınamadı. Lütfen tekrar deneyin.');
        return;
      }
      const body = {
        paymentType,
        paymentInformation: { cardBin: getPaymentCardBin() || undefined },
      };
      const { data: result } = await Requester.post<GenericResponse<OrderResponse>>(
        Endpoints.START_ORDER,
        body,
      );
      if (!result.data.order || !result.data.order.paymentInformation?.requestReferenceNo) {
        aError('Sipariş başlatılamadı. Lütfen tekrar deneyin.');
        return;
      }

      setOrderId(result.data.order.paymentInformation?.requestReferenceNo);

      const paylod = {
        accountKey: session.token.AccountKey,
        userId: session.token.UserId,
        additionalParams: {},
        customParameters: {},

        paymentType: 'sale',
        requestReferenceNo: result.data.order.paymentInformation?.requestReferenceNo,
        amount: safeMultiply(masterpassChargeTl(result.data.order), 100),
        cardAlias: selectedCard.cardAlias,
        orderNo: result.data.order.paymentInformation?.requestReferenceNo,
        terminalGroupId: session.service.terminalGroupId,
        Secure3dType: 'Model3D',
      };
      window.Masterpass.paymentService.payment(paylod, mfsResponseHandler);
    } catch (error) {
      if (isSenderMissing(error)) {
        showSenderMissing(error);
        return;
      }
      failPaySession(error);
    } finally {
      payAttemptRef.current = false;
    }
  };

  useEffect(() => {
    getCards();
  }, [serviceData, isSdkReady, isLoading]);

  useEffect(() => {
    if (selectedCard?.cardBin) {
      syncCardDiscountByBin(selectedCard.cardBin);
      return;
    }
    if (appliedCardBinRef.current) {
      syncCardDiscountByBin(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCard]);

  const processVerification = async (response: any) => {
    setIsPaying(true);
    try {
      response.serviceCall = true;
      response.oid = orderIdRef?.current;
      const { data: result } = await Requester.post<GenericResponse<any>>(
        Endpoints.MASTERPASS_COMPLETE_PAYMENT,
        response,
      );
      if (result?.data?.status) {
        router.replace(WebLink.ORDER_RECEIVED);
        return;
      }
      failPayment(result?.data?.vposErrorMessage);
    } catch (error) {
      aError(ErrorHandler.getMessage(error).message, error);
    }
  };

  const mfsResponseHandler = (status, response) => {
    setDialogStep(addCardDialogSteps.noForm);

    if (response?.statusCode !== 200 && response?.exception?.message) {
      failPayment(response?.exception?.message, [
        response?.responseCode,
        response?.exception?.code,
      ]);
      return;
    }

    if (
      response?.responseCode === '"ERROR_CALLED_OTP_SERVICE_ONE_MORE_TIME_BEFORE_TIMEOUT_EXPIRED"'
    ) {
      logB('otp-timeout', 'Lütfen bir süre sonra tekrar deneyin.', 'unexpected');
      toast({
        variant: 'destructive',
        description: 'Lütfen bir süre sonra tekrar deneyin.',
      });
      setIsPaying(false);
      return;
    }

    switch (response.responseCode || response.result.responseCode) {
      case '0000':
        if (
          flowTypeRef?.current === flowTypes.payment ||
          flowTypeRef?.current === flowTypes.payAndLink
        ) {
          setVerifyingOTP(false);
          setVerifyingCVV(false);
          processVerification(response);
        } else {
          if (flowTypeRef?.current === flowTypes.addCard) {
            setCardCandidate(response?.result?.cardUniqueNumber);
          }
          if (isConnecting) {
            setIsUserConnected(true);
          }
          getCards();
          clearStates();
          setIsPaying(false);
        }
        break;
      case '5001':
        showOTPForm();
        break;
      case '5008':
        showOTPForm();
        break;
      case '5009':
        // window.location.assign(response.urlIFrame + 'jwttoken=' + jwtToken + '&returnUrl=' + 'Your Return URL');
        break;
      case '5010':
        if (flowTypeRef?.current === flowTypes.payment) {
          window.location.assign(`${response.url3d}&returnUrl=${serviceReturnUrl}`);
        }
        break;
      case '5012':
        // const formValues = Utils.formDataToJson(document.forms["purchase-form"]);
        // Masterpass.creditService.overdraftQuery(formValues, mfsResponseHandler);
        break;
      case '5013':
        setCvvVCount((prev) => prev + 1);
        setVerifyingCVV(false);
        setIsPaying(false);
        setDialogStep(addCardDialogSteps.cvvForm);
        break;
      default:
        if (flowTypeRef?.current !== flowTypes.payment) {
          setDialogStep(addCardDialogSteps.cardForm);
        }
        failPayment(
          response?.result?.responseDescription ||
            response?.result?.description ||
            response?.exception?.message ||
            response?.description,
          [response?.responseCode, response?.result?.responseCode, response?.exception?.code],
        );
        break;
    }
  };

  function mfsRemoveResponseHandler(status: number, response: any) {
    if (status === 200) {
      getCards();
    }
    clearStates();
  }

  const showAddCardForm = () => {
    clearStates();
    if (!isSdkReady) {
      return;
    }
    setFlowType(flowTypes.addCard);
    setDialogStep(addCardDialogSteps.cardForm);
  };

  const onPayAndRegister = async (values: any) => {
    if (milesHoldClosed || payAttemptRef.current) return;
    payAttemptRef.current = true;
    clearStates();
    setTypeForGTM();
    setIsPaying(true);
    setFlowType(flowTypes.payAndLink);
    try {
      const session = await takePaySession();
      if (!window?.Masterpass) {
        aError('Masterpass hazır değil. Lütfen tekrar deneyin.');
        return;
      }
      if (!session?.token?.AccountKey || !session.service) {
        aError('Ödeme oturumu alınamadı. Lütfen tekrar deneyin.');
        return;
      }

      const body = {
        paymentType,
        paymentInformation: { cardBin: getPaymentCardBin(values) || undefined },
      };
      const { data: result } = await Requester.post<GenericResponse<OrderResponse>>(
        Endpoints.START_ORDER,
        body,
      );
      if (!result.data.order || !result.data.order.paymentInformation?.requestReferenceNo) {
        aError('Sipariş başlatılamadı. Lütfen tekrar deneyin.');
        return;
      }

      setOrderId(result.data.order.paymentInformation?.requestReferenceNo);

      const referenceNumber = Date.now();
      const paylod = {
        accountKey: session.token.AccountKey,
        accountKeyType: 'Msisdn',
        userId: session.token.UserId,
        requestReferenceNumber: referenceNumber,
        terminalGroupId: session.service?.terminalGroupId,
        cardNumber: values.cardNumber,
        expiryDate: `${values.expirationYear}${values.expirationMonth}`,
        cardAlias: values.cardAlias || 'Kredi Kartı',
        cardHolderName: values.fullname,
        cvc: values.cvv,
        paymentType: 'sale',
        requestReferenceNo: result.data.order.paymentInformation?.requestReferenceNo,
        amount: safeMultiply(masterpassChargeTl(result.data.order), 100),
        orderNo: result.data.order.paymentInformation?.requestReferenceNo,
        Secure3dType: 'Model3D',
      };
      window.Masterpass.paymentService.registerAndPurchase(paylod, mfsResponseHandler);
    } catch (error) {
      if (isSenderMissing(error)) {
        showSenderMissing(error);
        return;
      }
      failPaySession(error);
    } finally {
      payAttemptRef.current = false;
    }
  };

  const onSubmit = async (values: any) => {
    if (milesHoldClosed || payAttemptRef.current) return;
    payAttemptRef.current = true;
    clearStates();
    setTypeForGTM();
    setIsPaying(true);
    setFlowType(flowTypes.payment);
    try {
      const session = await takePaySession();
      if (!window?.Masterpass) {
        aError('Masterpass hazır değil. Lütfen tekrar deneyin.');
        return;
      }
      if (!session?.token?.AccountKey || !session.service) {
        aError('Ödeme oturumu alınamadı. Lütfen tekrar deneyin.');
        return;
      }

      const body = {
        paymentType,
        paymentInformation: { cardBin: getPaymentCardBin(values) || undefined },
      };
      const { data: result } = await Requester.post<GenericResponse<OrderResponse>>(
        Endpoints.START_ORDER,
        body,
      );
      if (!result.data.order || !result.data.order.paymentInformation?.requestReferenceNo) {
        aError('Sipariş başlatılamadı. Lütfen tekrar deneyin.');
        return;
      }

      setOrderId(result.data.order.paymentInformation?.requestReferenceNo);

      const referenceNumber = Date.now();
      const paylod = {
        accountKey: session.token.AccountKey,
        accountKeyType: 'Msisdn',
        userId: session.token.UserId,
        requestReferenceNumber: referenceNumber,
        terminalGroupId: session.service?.terminalGroupId,
        cardNumber: values.cardNumber,
        expiryDate: `${values.expirationYear}${values.expirationMonth}`,
        cardHolderName: values.fullname,
        cvc: values.cvv,
        paymentType: 'sale',
        requestReferenceNo: result.data.order.paymentInformation?.requestReferenceNo,
        amount: safeMultiply(masterpassChargeTl(result.data.order), 100),
        orderNo: result.data.order.paymentInformation?.requestReferenceNo,
        Secure3dType: 'Model3D',
      };
      window.Masterpass.paymentService.directPayment(paylod, mfsResponseHandler);
    } catch (error) {
      if (isSenderMissing(error)) {
        showSenderMissing(error);
        return;
      }
      failPaySession(error);
    } finally {
      payAttemptRef.current = false;
    }
  };

  const anonymousPart = (
    <>
      <MasterpassCardForm
        addingCard={addingCard}
        fullnamePreset={resolveSenderFullName()}
        onOpenChange={() => {}}
        onSubmit={onSubmit}
        open={true}
        forPayment={true}
        userIsAnonymous={true}
        setPaying={setIsPaying}
        paying={isPaying}
        paymentActionLabel={paymentSubmitLabel}
        onCardNumberChange={handleCardNumberChange}
        checkoutB={checkoutB}
      />
    </>
  );

  const notConnectedPart = (
    <MasterpassCardForm
      addingCard={addingCard}
      fullnamePreset={resolveSenderFullName()}
      onOpenChange={() => {}}
      onSubmit={onPayAndRegister}
      open={true}
      forPayment={true}
      userIsAnonymous={false}
      setPaying={setIsPaying}
      paying={isPaying}
      paymentActionLabel={paymentSubmitLabel}
      onCardNumberChange={handleCardNumberChange}
      checkoutB={checkoutB}
    />
  );

  const paymentPart = (
    <>
      {cards.map((card, index) => {
        return (
          <div
            className="flex gap-5 flex-row rounded-xl border-hzc_border_secondary border bg-transparent h-fit p-5 w-full items-center"
            key={`card_${index}`}
          >
            <div
              onClick={() => {
                setActiveCardNumber('');
                setSelectedCard(card);
              }}
              className={`border-2 cursor-pointer ${
                selectedCard?.uniqueCardNumber === card.uniqueCardNumber
                  ? 'border-hzc_purple_500'
                  : 'border-hzc_beige_700'
              } w-5 h-5 rounded-full justify-center content-center`}
            >
              {selectedCard?.uniqueCardNumber === card.uniqueCardNumber && (
                <div className="w-3 h-3 rounded-full bg-hzc_purple_500 mx-auto my-auto" />
              )}
            </div>
            <SmartImage
              className="inline cursor-pointer"
              src="/masterpass_logo.svg"
              alt="masterpass by mastercard"
              width={50}
              height={32}
              onClick={() => setSelectedCard(card)}
              imageType="custom"
            />
            <div className="flex-1 cursor-pointer" onClick={() => setSelectedCard(card)}>
              <div>{card.cardAlias}</div>
              <div>{formatCreditCardNumber(card.maskedCardNumber)}</div>
            </div>
            {removingCardAlias === card.cardAlias && isRemovingCardAlias ? (
              <Spinner className="size-5 text-black" />
            ) : (
              <Trash
                className="h-4 w-4 cursor-pointer duration-300 hover:scale-110 hover:text-red-500 my-auto"
                onClick={() => startRemoval(card.cardAlias)} /** removeCard(card.cardAlias) */
              />
            )}
          </div>
        );
      })}
      <div className="flex flex-row justify-between w-full">
        <Button
          className={`w-auto ${isSdkReady ? ' bg-hzc_purple_500 hover:bg-hzc_purple_500 hover:scale-105 transition-all duration-300' : ' bg-gray-500 hover:bg-gray-500'} rounded-full gap-2`}
          onClick={showAddCardForm}
        >
          <div className="text-hzc_beige_100 w-full flex flex-row items-center gap-3">
            Yeni Kart Ekle{' '}
            <PlusIcon className="text-xs group-hover:text-sm transition-all duration-300" />
          </div>
        </Button>
        <Button
          type="button"
          disabled={isPaying || !selectedCard}
          className={`w-auto ${selectedCard ? ' bg-hzc_purple_500 hover:bg-hzc_purple_500 hover:scale-105 transition-all duration-300' : ' bg-gray-500 hover:bg-gray-500'} rounded-full gap-2`}
          onClick={payWithCard}
        >
          <div className="text-hzc_beige_100 w-full flex flex-row items-center gap-3">
            {isPaying
              ? paymentSubmitLabel === APPOINTMENT_PRIMARY_CTA
                ? 'Randevu alınıyor...'
                : 'Ödeme yapılıyor...'
              : paymentSubmitLabel || 'Ödeme Yap'}
            {isPaying ? (
              <Spinner className="size-6 text-white" />
            ) : (
              <CreditCard className="text-xs group-hover:text-sm transition-all duration-300" />
            )}
          </div>
        </Button>
      </div>
    </>
  );

  const spinnerPart = <Spinner className="size-20 text-black" />;

  let visiblePart = spinnerPart;

  if (
    !isLoading &&
    !isItLoading &&
    !isConnecting &&
    (cardsGotten || userIsAnonymous) &&
    (dialogStep === addCardDialogSteps.noForm || userIsAnonymous || cards?.length > 0)
  ) {
    if (userIsAnonymous || !isUserConnected) {
      visiblePart = anonymousPart;
    } else if (cards.length === 0 /* pay and register!!! || !isUserConnected */) {
      visiblePart = notConnectedPart;
    } else {
      visiblePart = paymentPart;
    }
  } else {
    visiblePart = spinnerPart;
  }

  if (milesHoldClosed && !isPaying && !paymentError) return null;

  return (
    <div
      className={`flex min-h-40 flex-col gap-4 text-sm ${checkoutB ? '' : 'items-center justify-center'}`}
    >
      {/* <div className="fixed h-full w-full top-0 left-0 bg-hzc_purple_500 opacity-20 z-[60]"></div> */}
      <Script
        src="/masterpass-javascript-sdk-web.min.js"
        strategy="lazyOnload"
        onLoad={() => masterPassSDKLoaded(true)}
        onError={() => masterPassSDKLoaded(true)}
      />
      {!checkoutB ? (
        <div className="rounded-xl border-hzc_border_secondary border bg-transparent h-fit p-5 text-center w-full">
          <div className="max-w-md md:max-w-xl h-fit text-center m-auto">
            <div>
              Kart bilgileriniz Mastercard’ın dijital ödeme altyapısı olan{' '}
              <a
                className="text-[#01B0EF]"
                href="https://www.mastercard.com.tr/tr-tr/tuketiciler/odeme-secenekleri/masterpass.html"
                target="_blank"
              >
                Masterpass
              </a>
              ’te güvenle saklanmaktadır.
            </div>
            <div className="mt-5">
              <SmartImage
                className="inline"
                src="/masterpass.svg"
                alt="masterpass by mastercard"
                width={149}
                height={32}
                imageType="custom"
              />
            </div>
          </div>
        </div>
      ) : null}
      {/* isSdkReady ? (
          <p>SDK is ready! You can now make API calls.</p>
      ) : (
          <p>Loading SDK...</p>
      ) */}
      {visiblePart}
      <SavedCardsLoadFailedDialog
        open={cardsFailMode === 'choose'}
        retrying={cardsRetrying}
        onRetry={retryListSavedCards}
        onPayWithCard={choosePayWithCardDetails}
      />
      {!userIsAnonymous && !isLoading && (
        <>
          <MasterpassAddCardDialog
            open={dialogStep === addCardDialogSteps.cardForm}
            onOpenChange={onPopupClose}
            setAddingCard={setAddingCard}
            fullnamePreset={resolveSenderFullName()}
            addingCard={addingCard}
            mfsResponseHandler={mfsResponseHandler}
            setPaying={setIsPaying}
          />
          <MasterpassOTPDialog
            open={dialogStep === addCardDialogSteps.otpForm}
            onOpenChange={onPopupClose}
            setVerifyingOTP={setVerifyingOTP}
            verifyingOTP={verifyingOTP}
            mfsResponseHandler={mfsResponseHandler}
            vCount={otpVCount}
            setPaying={setIsPaying}
          />
          <MasterpassCVVDialog
            open={dialogStep === addCardDialogSteps.cvvForm}
            onOpenChange={onPopupClose}
            setVerifyingCVV={setVerifyingCVV}
            verifyingCVV={verifyingCVV}
            mfsResponseHandler={mfsResponseHandler}
            vCount={cvvVCount}
            setPaying={setIsPaying}
          />
          <MasterpassRemoveCardDialog
            open={dialogStep === addCardDialogSteps.removeCardForm}
            onOpenChange={onPopupClose}
            cardCount={cards.length}
            cardAlias={removingCardAlias || ''}
            onRemoveAction={(alias: string) => {
              removeCard(alias);
            }}
            vCount={removeVCount}
          />
          <MasterpassConnectDialog
            open={dialogStep === addCardDialogSteps.connectAccount}
            onOpenChange={() => setDialogStep(addCardDialogSteps.noForm)}
            isConnecting={isConnecting}
            phone={tokenData?.AccountKey}
            connectHandler={linkToMerchant}
            rejectHandler={rejectConnection}
          />
        </>
      )}
      <MasterpassLoadingDialog
        open={(isPaying || isRemovingCardAlias) && !paymentError}
        content={
          <div className="text-center text-white">
            <div>İşlem devam ediyor.</div>
            <div>Lütfen bekleyin...</div>
          </div>
        }
      />
      {checkoutB ? (
        <CheckoutBPaymentErrorDialog message={paymentError} onClose={() => setPaymentError(null)} />
      ) : null}
      <CheckoutPaymentProblem paymentType={paymentType} paymentTypes={paymentTypes} />
    </div>
  );
}
